import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * Popups the theme editor keeps open, by block id: the editor renders the block again on every change,
 * and the new one opens again while one of its blocks is selected.
 * @type {Set<string>}
 */
const openInEditor = new Set();

/**
 * Contact popup (blocks/contact-popup.liquid): a theme Button opens a dialog with theme blocks inside
 * (a Text title and the theme's contact form in the preset).
 *
 * - The Button is a link (the Button block always renders one), so its click is taken over here: it
 *   opens the dialog instead of following the link.
 * - The first heading inside names the dialog for assistive tech; without one, the theme's text does.
 * - Shopify's contact form reloads the page when it is sent. When it comes back with its success or
 *   error message (blocks/contact-form.liquid), the dialog opens again by itself to show it.
 * - When the form doesn't fit (short phones), its scroll box is the first thing the browser can focus
 *   on open. The focus goes to the first field instead, as when it fits.
 * - Shopify runs hCaptcha when the form is sent. When it has to show a challenge, the challenge goes in
 *   the page, under the modal dialog, where it can't be used. The dialog closes to let it through; once
 *   it's solved, the form is sent and the dialog opens again with the result.
 * - In the theme editor, the dialog opens when the popup or a block inside it is selected, and closes
 *   when something else is.
 *
 * @extends {DialogComponent}
 */
class ContactPopupComponent extends DialogComponent {
  /** @type {MutationObserver | null} */
  #captchaObserver = null;

  connectedCallback() {
    super.connectedCallback();

    for (const trigger of this.#triggers) {
      trigger.setAttribute('aria-haspopup', 'dialog');
    }

    this.#labelDialog();

    this.addEventListener('click', this.#handleTriggerClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.addEventListener('submit', this.#handleSubmit, true);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleEditorSelect);
    }

    if (
      this.querySelector('.contact-form__success, .contact-form__error') ||
      (window.Shopify?.designMode && openInEditor.has(this.#blockId))
    ) {
      this.showDialog();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleTriggerClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.removeEventListener('submit', this.#handleSubmit, true);
    document.removeEventListener('shopify:block:select', this.#handleEditorSelect);
    this.#captchaObserver?.disconnect();
  }

  /** @returns {string} */
  get #blockId() {
    return this.dataset.blockId ?? '';
  }

  /**
   * The links and buttons of the trigger area (outside the dialog).
   * @returns {HTMLElement[]}
   */
  get #triggers() {
    return [...this.querySelectorAll('[data-contact-popup-trigger] :is(a, button)')];
  }

  /**
   * Names the dialog after its first heading. The `aria-label` of the dialog stays as the fallback.
   */
  #labelDialog() {
    const { dialog } = this.refs;
    const heading = dialog?.querySelector('.contact-popup__inner :is(h1, h2, h3, h4, h5, h6)');
    if (!dialog || !heading) return;

    if (!heading.id) heading.id = `ContactPopupHeading-${this.#blockId}`;
    dialog.setAttribute('aria-labelledby', heading.id);
  }

  /**
   * Opens the dialog from the trigger instead of following its link.
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-contact-popup-trigger] :is(a, button)');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    this.showDialog();
  };

  /**
   * Moves the focus from the scroll box to the first field.
   */
  #handleOpen = () => {
    const scrollBox = this.querySelector('.contact-popup__inner');
    if (!scrollBox || document.activeElement !== scrollBox) return;

    const firstField = scrollBox.querySelector(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
    );
    if (firstField instanceof HTMLElement) firstField.focus({ preventScroll: true });
  };

  #handleClose = () => {
    openInEditor.delete(this.#blockId);
  };

  /**
   * Theme editor: opens the dialog for the popup and the blocks inside it, closes it for anything else.
   * @param {Event} event
   */
  #handleEditorSelect = (event) => {
    const { dialog } = this.refs;
    const target = event.target;
    if (!dialog || !(target instanceof Node)) return;

    if (target === this || dialog.contains(target)) {
      openInEditor.add(this.#blockId);
      this.showDialog();
    } else if (dialog.open) {
      this.closeDialog();
    }
  };

  /**
   * Watches for the hCaptcha challenge once the form is sent, and closes the dialog when it shows.
   */
  #handleSubmit = () => {
    this.#captchaObserver?.disconnect();
    this.#captchaObserver = new MutationObserver(() => {
      if (!this.#isCaptchaChallengeVisible()) return;

      this.#captchaObserver?.disconnect();
      this.closeDialog();
    });
    this.#captchaObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
  };

  /**
   * The challenge is an hCaptcha frame outside the popup (the one inside is the invisible widget).
   * @returns {boolean}
   */
  #isCaptchaChallengeVisible() {
    return [...document.querySelectorAll('iframe[src*="hcaptcha"]')].some(
      (frame) =>
        !this.contains(frame) &&
        frame.getBoundingClientRect().width > 0 &&
        getComputedStyle(frame).visibility === 'visible'
    );
  }
}

if (!customElements.get('contact-popup-component')) {
  customElements.define('contact-popup-component', ContactPopupComponent);
}
